import React, { useEffect, useMemo, useState } from "react";
import { Card, Form, Table } from "react-bootstrap";
import usePalette from "../../../../hooks/usePalette";
import { useDispatch, useSelector } from "react-redux";
import numeral from "numeral";
import { IconButton, Tooltip } from "@mui/material";
import CurrencyInput from "react-currency-input-field";
import Select, { components } from "react-select";
import { isEmpty } from "lodash";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { customStylesForm } from "../../../../components/common/customStyleSelect";

// Icons
import Icon from "@mdi/react";
import { mdiPlusCircle, mdiMinusCircle } from "@mdi/js";

import DraggableNewScopeRow from "./dnd-components/DraggableNewScopeRow";
import DroppableYearRow from "./dnd-components/DroppableYearRow";
import DraggableRow from "./dnd-components/DraggableRow";

import {
  addScopeProject,
  createCertifyFinancial,
  removeScopeProject,
  updateRevisedProjectScope,
  moveCertifyFinancialItem,
} from "../../../../redux/slices/utilsSlice";
import {
  addProjectScopeByYear,
  updateCeilingAmount,
  updateProjectScopeByYear,
} from "../../../../redux/slices/certify/projectsSlice";

const ScopeProjectByYear = ({ isEqualAmount }) => {
  const dispatch = useDispatch();
  const palette = usePalette();

  const {
    project,
    revisedProjectScope,
    projectScopeItems,
    rmkActiveApplication,
    dndLoading,
    inputLoading,
  } = useSelector((state) => ({
    project: state.certifyReducer.project.data,
    revisedProjectScope: state.utilsReducer.certifyFinancial,
    projectScopeItems: state.utilsReducer.dropdownItems.projectScopeItems,
    rmkActiveApplication: state.rmkReducer.rmkActiveApplication,
    dndLoading: state.utilsReducer.dndLoading,
    inputLoading: state.utilsReducer.inputLoading,
  }));

  const [newScopeByYears, setNewScopeByYears] = useState([]);

  const scopeByYears = useMemo(() => {
    if (isEmpty(revisedProjectScope)) return;

    const years = [
      rmkActiveApplication.first_year,
      rmkActiveApplication.second_year,
      rmkActiveApplication.third_year,
      rmkActiveApplication.fourth_year,
      rmkActiveApplication.fifth_year,
    ];

    const ceilingFlowDetails = revisedProjectScope
      ?.filter((obj) => !isEmpty(obj.ceiling_flow_details))
      ?.flatMap((obj) =>
        obj.ceiling_flow_details.map((subObj) => ({
          ...subObj,
          cost_description: obj.cost_description,
        })),
      );

    const groupedByYear = ceilingFlowDetails?.reduce((acc, item) => {
      const { year } = item;

      if (years.includes(year)) {
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push(item);
      }
      return acc;
    }, {});

    const result = years.map((year) => ({
      year: Number(year), // or `Number(year)` if you prefer numbers
      children: groupedByYear[year] || [], // default to empty array
    }));

    return result;
  }, [revisedProjectScope, rmkActiveApplication]);

  const sumCeiling = useMemo(() => {
    if (scopeByYears) {
      const sum = scopeByYears
        ?.flatMap((obj) => obj.children)
        ?.reduce((acc, obj) => acc + parseFloat(obj.amount), 0);

      return sum;
    }
  }, [scopeByYears]);

  const balanceCeiling = useMemo(() => {
    if (scopeByYears) {
      const balance = revisedProjectScope;
      return balance;
    }
  }, [scopeByYears]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 0, // Very small distance to trigger drag immediately on click
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 0, // No delay for touch devices
        tolerance: 5,
      },
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!active || !over) {
      console.log("No valid drop target");
      return;
    }

    // Preserve the original row type
    const activeValue = {
      id: active.id,
      rect: active.rect,
      data: {
        current: {
          ...active.data.current, // Keep all original data
          values: {
            ...active.data.current.values,
            // Only update the year if this is a new scope item
            ...(active.data.current.row === "new_scope" && {
              year: null,
              amount: "0",
              revised_amount: "0",
              cost_required: "0",
              is_rejected: false,
              deleted_at: null,
              ceiling_flow_detail_stages: [],
              ceiling_flow_detail_notes: [],
            }),
          },
        },
      },
    };

    if (activeValue.data.current.values.year === Number(over.id)) {
      console.log("Dropped on same element");
      return;
    }

    if (
      over.data?.current?.accepts?.includes(activeValue.data?.current?.type)
    ) {
      handleDropToYear(activeValue, over);
    } else {
      console.log("Invalid drop target");
    }
  };

  const handleDropToYear = (draggedItem, targetRow) => {
    const { id: activeId, data: activeData } = draggedItem;
    const activeValues = activeData.current.values;
    const dropAtYear = targetRow.data.current.year;

    // For items from the "unassigned" section (new scope)
    if (activeData.current.row === "new_scope") {
      const newFlowDetail = {
        financial_detail_id: activeId,
        year: dropAtYear,
        amount: "0", // Default amount when moving from unassigned
        revised_amount: "0",
        cost_required: "0",
        is_rejected: false,
        deleted_at: null,
        ceiling_flow_detail_stages: [],
        ceiling_flow_detail_notes: [],
        cost_description: activeValues.cost_description,
      };
      const newValues = {
        project_id: project.id,
        oldValues: activeValues,
        newValues: newFlowDetail,
        targetRow: dropAtYear,
      };
      console.log(newValues);
      dispatch(addProjectScopeByYear(newValues));
      // dispatch(
      //   moveCertifyFinancialItem({
      //     oldValues: activeValues,
      //     newValues: newFlowDetail,
      //     targetRow: dropAtYear,
      //   }),
      // );

      return;
    }

    // For existing items being moved between years
    if (activeValues.year !== dropAtYear) {
      const newFlowDetail = {
        ...activeValues,
        year: dropAtYear,
      };
      const newValues = {
        project_id: project.id,
        ceiling_flow_id: newFlowDetail.id,
        oldValues: activeValues,
        newValues: newFlowDetail,
        targetRow: dropAtYear,
      };
      dispatch(updateProjectScopeByYear(newValues));
      // dispatch(
      //   moveCertifyFinancialItem({
      //     oldValues: activeValues,
      //     newValues: newFlowDetail,
      //     targetRow: dropAtYear,
      //   }),
      // );
    }
  };

  const handleUpdateOnAction = async (props) => {
    const { ceiling_id, amount } = props;

    const params = {
      ceiling_flow_id: ceiling_id,
      project_id: project.id,
      amount: amount,
    };

    await dispatch(updateCeilingAmount(params));
  };

  return (
    <>
      <Card style={{ cursor: dndLoading ? "progress" : "default" }}>
        {/* Revised Project Scope */}
        <Card.Body>
          <div className='mb-3'>
            <h5>SENARAI ALIRAN SILING</h5>
            <p>
              Sila <span className='fst-italic fw-bolder'>Drag and Drop</span>{" "}
              skop projek ke tahun yang dipilih.
            </p>
          </div>
          <div className='table-responsive'>
            <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
              <Table bordered size='sm' style={{ fontSize: 12, width: "100%" }}>
                <thead className='text-center align-middle custom-table-head'>
                  <tr>
                    <th style={{ width: "5%" }}>BIL.</th>
                    <th style={{ width: "80%" }}>KOMPONEN PROJEK</th>
                    <th style={{ width: "15%" }}>SILING DIPOHON (RM)</th>
                  </tr>
                </thead>
                <tbody className='text-uppercase' style={{ fontWeight: 500 }}>
                  <>
                    <tr
                      style={{ backgroundColor: "#f5f5f5" }}
                      className='fw-bolder'
                    >
                      <td />
                      <td className='ps-2'>
                        SENARAI SKOP YANG BELUM DITENTUKAN PERLAKSANAAN SECARA
                        TAHUNAN
                      </td>
                      <td className='text-center'>BAKI SILING</td>
                    </tr>

                    {(() => {
                      // Total ceiling for all scopes
                      const totalCeiling = revisedProjectScope?.reduce(
                        (acc, obj) => acc + parseFloat(obj.ceiling || 0),
                        0,
                      );

                      // Total allocated from all ceiling_flow_details
                      const totalAllocated = revisedProjectScope
                        ?.flatMap((obj) => obj.ceiling_flow_details || [])
                        ?.reduce(
                          (acc, detail) => acc + parseFloat(detail.amount || 0),
                          0,
                        );

                      const remainingBalance = totalCeiling - totalAllocated;

                      if (remainingBalance === 0) {
                        return (
                          <tr>
                            <td colSpan={5} className='text-center'>
                              Tiada rekod
                            </td>
                          </tr>
                        );
                      }

                      const filteredAndSortedScopes = revisedProjectScope
                        ?.toSorted((a, b) => a.id - b.id)
                        ?.filter((obj) => {
                          const totalCeiling = parseFloat(obj.ceiling || 0);
                          const allocated = obj.ceiling_flow_details?.reduce(
                            (acc, item) => acc + parseFloat(item.amount || 0),
                            0,
                          );
                          return totalCeiling - allocated !== 0;
                        });

                      return filteredAndSortedScopes?.map((obj, idx) => {
                        const totalCeiling = parseFloat(obj.ceiling || 0);
                        const allocated = obj.ceiling_flow_details?.reduce(
                          (acc, item) => acc + parseFloat(item.amount || 0),
                          0,
                        );
                        const balance = totalCeiling - allocated;

                        return (
                          <DraggableNewScopeRow
                            key={obj.id}
                            id={obj.id}
                            data={obj}
                          >
                            <td className='text-center'>{idx + 1}.</td>
                            <td className='text-wrap'>
                              {obj.cost_description || (
                                <span className='text-error'>
                                  Sila Pilih Skop Projek
                                </span>
                              )}
                            </td>
                            <td
                              className='text-end pe-3'
                              style={{
                                color: isEqualAmount.isNegativeValue
                                  ? "#f44336"
                                  : "",
                                fontWeight: isEqualAmount.isNegativeValue
                                  ? "700"
                                  : "",
                              }}
                            >
                              {numeral(balance).format("0,0.00")}
                            </td>
                          </DraggableNewScopeRow>
                        );
                      });
                    })()}
                  </>

                  {scopeByYears
                    ?.toSorted((a, b) => a.index - b.index)
                    ?.map((obj, idx) => {
                      const { children } = obj;

                      const sumCeilingByYear = children.reduce(
                        (acc, item) => (acc += parseFloat(item.amount)),
                        0,
                      );

                      return (
                        <React.Fragment key={obj.year}>
                          <DroppableYearRow year={obj.year} colSpan={4} />

                          {children
                            ?.toSorted(
                              (a, b) =>
                                a.financial_detail_id - b.financial_detail_id,
                            )
                            ?.map((subObj, subIdx) => {
                              const findMatch =
                                isEqualAmount.balanceCeilingByScope?.find(
                                  (item) =>
                                    item.financial_detail_id ===
                                    subObj.financial_detail_id,
                                );
                              const scopeCeiling = revisedProjectScope.find(
                                (item) =>
                                  item.id === subObj.financial_detail_id,
                              ).ceiling;

                              const checkBalance =
                                parseFloat(scopeCeiling) -
                                parseFloat(findMatch.totalCeiling);

                              return (
                                <DraggableRow
                                  key={subObj.id}
                                  id={subObj.id}
                                  data={subObj}
                                >
                                  <td className='text-center'>{subIdx + 1}.</td>
                                  <td
                                    style={{
                                      color:
                                        checkBalance !== 0 ? "#f44336" : "",
                                    }}
                                  >
                                    {subObj.cost_description}
                                  </td>
                                  <td
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onTouchStart={(e) => e.stopPropagation()}
                                  >
                                    <Form.Group
                                      className='w-100'
                                      style={{
                                        cursor: inputLoading
                                          ? "progress"
                                          : "default",
                                      }}
                                    >
                                      <Form.Control
                                        className={`text-end text-truncate`}
                                        as={CurrencyInput}
                                        min={0}
                                        name='amount'
                                        autoComplete='off'
                                        decimalScale={2}
                                        decimalsLimit={2}
                                        placeholder='0.00'
                                        value={subObj.amount}
                                        onValueChange={(value) => {
                                          const info = {
                                            year: obj.year,
                                            financial_detail_id:
                                              subObj.financial_detail_id,
                                            cost_description:
                                              subObj.cost_description,
                                          };
                                          const newValues = {
                                            id: info,
                                            value: value,
                                            type: "ceiling-flow",
                                          };
                                          dispatch(
                                            updateRevisedProjectScope(
                                              newValues,
                                            ),
                                          );
                                        }}
                                        onBlur={async () => {
                                          const props = {
                                            ceiling_id: subObj.id,
                                            amount: subObj.amount,
                                          };
                                          handleUpdateOnAction(props);
                                        }}
                                        disabled={inputLoading}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            const props = {
                                              ceiling_id: subObj.id,
                                              amount: subObj.amount,
                                            };
                                            handleUpdateOnAction(props);
                                          }
                                          return;
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onTouchStart={(e) =>
                                          e.stopPropagation()
                                        }
                                      />
                                      <Form.Control.Feedback type='invalid'>
                                        Kos tidak boleh kurang dari siling
                                      </Form.Control.Feedback>
                                    </Form.Group>
                                  </td>
                                </DraggableRow>
                              );
                            })}
                          <tr>
                            <td colSpan={2} className='text-end pe-3 py-2'>
                              JUMLAH SILING
                            </td>
                            <td className='text-end pe-3'>
                              {numeral(sumCeilingByYear).format("0,0.00")}
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}

                  <tr style={{ borderTop: "double" }}>
                    <td colSpan={2} className='text-end pe-3'>
                      {isEqualAmount.isTrue && (
                        <span className='mx-1' style={{ color: "#f44336" }}>
                          Aliran siling
                          <span className='fw-bolder mx-1'>
                            melebihi/kurang
                          </span>
                          daripada siling kesuluruhan.
                        </span>
                      )}
                      JUMLAH KESELURUHAN SILING
                      <span className='fw-bolder ms-1'>(DISEMAK)</span>
                    </td>

                    <td className='text-end pe-3 fw-bolder'>
                      {numeral(sumCeiling).format("0,0.00")}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </DndContext>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default ScopeProjectByYear;
